import { create } from 'zustand';
import { BUILD_ID } from '../constants/appVersion';
import { flushAllBlobSyncs } from './syncBlob';
import { useCurrentView } from '../hooks/useCurrentView';

/**
 * ⭐⭐ A DEPLOY UNDER AN OPEN TAB (owner, 2026-10-04).
 *
 * Every deploy replaces the app's hashed chunks and deletes the old ones. A tab still
 * running the previous build then asks for a chunk that no longer exists the first
 * time it opens a view it had not loaded yet — and crashed with "Something went
 * wrong" ('text/html' is not a valid JavaScript MIME type: the host answered the
 * missing file with index.html). Three layers stop that:
 *
 *  1. NOTICE IT EARLY — poll `/build.json` (written by every build, served no-store)
 *     every few minutes and whenever the tab comes back; a different build → status
 *     `available`: a card that cannot be dismissed but does not block the work, and
 *     the NEXT view change reloads quietly into the new build.
 *  2. CATCH THE FAILURE — a chunk that fails to load (`vite:preloadError`, an
 *     unhandled rejection, or the ErrorBoundary) → status `required`: a full-screen
 *     "Metanoia has been updated" with Reload, never the generic crash screen.
 *  3. (server) a missing `/assets/*` file is a 404, so the failure is immediate and
 *     recognisable — `public/_redirects`.
 *
 * Every reload FLUSHES pending cloud writes first (local-first: an edit made seconds
 * ago may not have left the browser yet).
 */

export type UpdateStatus = 'current' | 'available' | 'required';

interface UpdateState {
  status: UpdateStatus;
  /** The build the server reported, when it differs from ours. */
  latest: { build: string; version: string } | null;
  markAvailable: (latest: { build: string; version: string }) => void;
  markRequired: () => void;
}

export const useAppUpdateStore = create<UpdateState>()(set => ({
  status: 'current',
  latest: null,
  // ⚠️ Never downgrades: `required` stays required.
  markAvailable: latest => set(s => ({ latest, status: s.status === 'required' ? 'required' : 'available' })),
  markRequired: () => set({ status: 'required' }),
}));

/**
 * The messages browsers give for a script that could not be loaded as a module.
 * Chrome/Edge · Safari · Firefox · the MIME refusal (Netlify's index.html at 200) ·
 * a CSS chunk that failed to preload.
 */
const CHUNK_ERROR = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /is not a valid JavaScript MIME type/i,
  /Unable to preload CSS/i,
  /ChunkLoadError|Loading chunk [\w-]+ failed/i,
];

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return CHUNK_ERROR.some(re => re.test(message));
}

/** A different build is live? `null` when it cannot tell (offline, no file, not JSON). */
export async function fetchLatestBuild(fetcher: typeof fetch = fetch): Promise<{ build: string; version: string } | null> {
  try {
    const res = await fetcher(`/build.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = (await res.json()) as { build?: unknown; version?: unknown };
    return typeof data.build === 'string' && data.build
      ? { build: data.build, version: typeof data.version === 'string' ? data.version : '' }
      : null;
  } catch {
    return null;
  }
}

/** Compares the live build with ours and records an update. Returns whether one exists. */
export async function checkForUpdate(current: string = BUILD_ID, fetcher: typeof fetch = fetch): Promise<boolean> {
  if (!current) return false;
  const latest = await fetchLatestBuild(fetcher);
  if (!latest || latest.build === current) return false;
  useAppUpdateStore.getState().markAvailable(latest);
  return true;
}

/* ── Reloading ─────────────────────────────────────────────────────────────── */

const GUARD_KEY = 'metanoia-update-reload';

/**
 * ⚠️⚠️ THE LOOP GUARD. An AUTOMATIC reload happens at most once per target build per
 * tab: if the page comes back still on the old build (a stale CDN edge, a broken
 * deploy), the next view change must not reload again, and again. The card stays and
 * the Reload button always works — only the quiet reload is limited.
 */
export function mayAutoReload(target: string): boolean {
  try {
    return sessionStorage.getItem(GUARD_KEY) !== target;
  } catch {
    return false; // storage refused — never risk a loop
  }
}

function rememberAutoReload(target: string) {
  try { sessionStorage.setItem(GUARD_KEY, target); } catch { /* refused — mayAutoReload already says no */ }
}

/** The one place a reload happens — replaceable in tests (jsdom cannot reload). */
export const pageReload = { run: () => window.location.reload() };

/** Waits for pending cloud writes (at most `ms`), then reloads. */
export async function reloadToLatest(ms = 2500, reload: () => void = () => pageReload.run()): Promise<void> {
  try {
    await Promise.race([flushAllBlobSyncs(), new Promise(r => setTimeout(r, ms))]);
  } catch {
    /* a failed flush still reloads — the writes stay queued locally and sync next load */
  }
  reload();
}

/* ── Wiring ────────────────────────────────────────────────────────────────── */

export const CHECK_EVERY_MS = 5 * 60 * 1000;

let started = false;

/**
 * Starts the three listeners. Production only: in dev the build id changes with every
 * commit and the dev server restarts itself (vite.config.ts), so there is nothing to
 * prompt about. Idempotent.
 */
export function startAppUpdateWatcher(options: { enabled?: boolean } = {}): () => void {
  const enabled = options.enabled ?? (!import.meta.env.DEV && !!BUILD_ID);
  if (!enabled || started || typeof window === 'undefined') return () => {};
  started = true;

  const check = () => { void checkForUpdate(); };

  // Layer 2: a chunk that failed to load. Vite's own event first; anything that slips
  // past it as an unhandled rejection second. ⚠️ Not `preventDefault()` — the import
  // must still reject, so React's boundary shows the update screen instead of a lazy
  // component rendering `undefined`.
  const onPreloadError = () => useAppUpdateStore.getState().markRequired();
  const onRejection = (e: PromiseRejectionEvent) => {
    if (isChunkLoadError(e.reason)) useAppUpdateStore.getState().markRequired();
  };
  const onVisible = () => { if (document.visibilityState === 'visible') check(); };

  window.addEventListener('vite:preloadError', onPreloadError);
  window.addEventListener('unhandledrejection', onRejection);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', check);
  const timer = window.setInterval(check, CHECK_EVERY_MS);

  // Layer 1b: once an update is known, the NEXT view change reloads into it — the URL
  // is already the new view's, so the reload lands exactly where they were going.
  const unsubscribe = useCurrentView.subscribe((state, prev) => {
    if (state.currentView === prev.currentView) return;
    const { status, latest } = useAppUpdateStore.getState();
    if (status !== 'available' || !latest || !mayAutoReload(latest.build)) return;
    rememberAutoReload(latest.build);
    void reloadToLatest();
  });

  check();

  return () => {
    started = false;
    window.removeEventListener('vite:preloadError', onPreloadError);
    window.removeEventListener('unhandledrejection', onRejection);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', check);
    window.clearInterval(timer);
    unsubscribe();
  };
}
